import { useCallback, useState, useEffect } from 'react';
import {
  useBlocker,
  useParams,
  Link,
  useLocation,
  useNavigate
} from 'react-router-dom';
import { toast } from 'react-toastify';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';

import { useTRPCRequest } from '../../../hooks/useTRPCRequest';
import { TRPCMethodEnum, TRPCResourceEnum } from '../../../api/trpcApi/types';
import { ContainedButton } from '../../../components/buttons';
import {
  LoadingModalComponent,
  UnsavedChangesModalComponent,
  ConfirmModalComponent
} from '../../../components/modals';
import { Title } from '../../../components/Title';
import { Chip } from '../../../components/chips';
import { SizeEnum } from '../../../types';

import { PartnerProduct, PartnerProductStyle } from '../types';
import { BaseStylesMultiSelect, PartnerStylesMultiSelect } from '../components';

import { useLabels } from '../../products/hooks';
import { useMaterials } from '../../materials/routes/hooks';
import { LabelSlots, ProductTemplateSelections } from '../../products/types';
import { formatLabelSlots } from '../../products/utils';
import { useStyles } from '../../styles/routes/hooks';
import {
  AddLabelsComponent,
  GraphComponent,
  TemplateSelections
} from '../../products/components';

// Interface for base product data
interface BaseProductData {
  product_id: string;
  system_name: string;
  display_name: string;
  style_ids: string[];
  all_styles: boolean;
  use_mixed_template: boolean;
  label_slots: Array<{
    product_label_slot_id?: string;
    options: Array<{
      label_id: string;
      quantity: number;
      material_ids: string[];
      coordinates: Array<{
        x: number;
        y: number;
        width: number;
        height: number;
      }>;
      endcap_text: string | null;
      endcap_is_footer: boolean | null;
      endcap_print_mode: string | null;
    }>;
  }>;
  template_selections: Partial<ProductTemplateSelections>[];
}

export function PartnerProductDetails() {
  const params = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { handleTRPCRequest } = useTRPCRequest();
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isSaving, setIsSaving] = useState<boolean>(false);

  const [unsavedChanges, setUnsavedChanges] = useState<boolean>(false);
  const unsavedChangesBlocker = useBlocker(unsavedChanges);

  const [partnerProduct, setPartnerProduct] = useState<PartnerProduct | null>(
    null
  );
  const [partnerName, setPartnerName] = useState<string>('');
  const [baseProduct, setBaseProduct] = useState<BaseProductData | null>(null);

  // Style selection state
  const [selectedBaseStyleIds, setSelectedBaseStyleIds] = useState<string[]>(
    []
  );
  const [selectedPartnerStyleIds, setSelectedPartnerStyleIds] = useState<
    string[]
  >([]);
  const [originalBaseStyleIds, setOriginalBaseStyleIds] = useState<string[]>(
    []
  );
  const [originalPartnerStyleIds, setOriginalPartnerStyleIds] = useState<
    string[]
  >([]);

  // Style selector open states
  const [baseStylesSelectOpen, setBaseStylesSelectOpen] = useState(false);
  const [partnerStylesSelectOpen, setPartnerStylesSelectOpen] = useState(false);

  // Label slots state (for display)
  const [labelSlots, setLabelSlots] = useState<LabelSlots>({});
  const [isOption, setIsOption] = useState<boolean>(false);
  const [selectedIndex, setSelectedIndex] = useState(0);

  // Reset confirmation
  const [showResetConfirm, setShowResetConfirm] = useState(false);

  // Hooks for labels, materials, and styles
  const { labels, isLoading: labelsLoading } = useLabels();
  // Materials hook is used by AddLabelsComponent internally
  useMaterials(undefined, true);
  const { styles: allBaseStyles } = useStyles({ filterType: 'base' });
  const { styles: allPartnerStyles } = useStyles({
    filterType: 'partner',
    partnerId: params.partner_id
  });

  // Determine navigation source
  const cameFromProduct = location.state?.from === 'product';

  // Parse label slots for display
  useEffect(() => {
    if (!baseProduct || !labels.length) return;

    const option =
      baseProduct.label_slots.length === 1 &&
      baseProduct.label_slots[0].options.length > 1;
    setIsOption(option);

    const formattedSlots = formatLabelSlots(labels, option, baseProduct);
    setLabelSlots(formattedSlots);
  }, [baseProduct, labels]);

  const syncFromApi = useCallback(async () => {
    setIsLoading(true);

    // Fetch partner info
    if (params.partner_id) {
      const { res: partner } = await handleTRPCRequest({
        method: TRPCMethodEnum.get,
        resourceType: TRPCResourceEnum.partners,
        requestBody: { partner_id: params.partner_id }
      });
      if (partner) {
        setPartnerName(partner.name);
      }
    }

    // Fetch product info (including label_slots and template_selections)
    if (params.product_id) {
      const { res: product } = await handleTRPCRequest({
        method: TRPCMethodEnum.get,
        resourceType: TRPCResourceEnum.products,
        requestBody: { product_id: params.product_id }
      });
      if (product) {
        setBaseProduct({
          product_id: product.product_id,
          system_name: product.system_name,
          display_name: product.display_name,
          style_ids: product.style_ids || [],
          all_styles: product.all_styles || false,
          use_mixed_template: product.use_mixed_template || false,
          label_slots: product.label_slots || [],
          template_selections: product.template_selections || []
        });
      }
    }

    // Fetch partner product
    if (params.partner_id && params.product_id) {
      const { res: fetchedPartnerProduct, error } = await handleTRPCRequest({
        method: TRPCMethodEnum.getByPartnerAndProduct,
        resourceType: TRPCResourceEnum.partnerProducts,
        requestBody: {
          partner_id: params.partner_id,
          product_id: params.product_id
        }
      });

      if (!error && fetchedPartnerProduct) {
        setPartnerProduct(fetchedPartnerProduct);

        // Fetch selected styles from junction table
        const { res: fetchedStyles } = await handleTRPCRequest({
          method: TRPCMethodEnum.listStyles,
          resourceType: TRPCResourceEnum.partnerProducts,
          requestBody: {
            partner_product_id: fetchedPartnerProduct.partner_product_id
          }
        });

        if (fetchedStyles) {
          // Separate base styles and partner styles
          const baseIds: string[] = [];
          const partnerIds: string[] = [];

          fetchedStyles.forEach((s: PartnerProductStyle) => {
            if (s.is_partner_style) {
              partnerIds.push(s.style_id);
            } else {
              baseIds.push(s.style_id);
            }
          });

          setSelectedBaseStyleIds(baseIds);
          setSelectedPartnerStyleIds(partnerIds);
          setOriginalBaseStyleIds(baseIds);
          setOriginalPartnerStyleIds(partnerIds);
        }
      }
    }

    setIsLoading(false);
  }, [params.partner_id, params.product_id, handleTRPCRequest]);

  useEffect(() => {
    if (!labelsLoading) {
      syncFromApi();
      setUnsavedChanges(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [labelsLoading]);

  // Check if there are unsaved changes
  useEffect(() => {
    const baseChanged =
      JSON.stringify([...selectedBaseStyleIds].sort()) !==
      JSON.stringify([...originalBaseStyleIds].sort());
    const partnerChanged =
      JSON.stringify([...selectedPartnerStyleIds].sort()) !==
      JSON.stringify([...originalPartnerStyleIds].sort());

    setUnsavedChanges(baseChanged || partnerChanged);
  }, [
    selectedBaseStyleIds,
    selectedPartnerStyleIds,
    originalBaseStyleIds,
    originalPartnerStyleIds
  ]);

  // Handle base styles change
  const handleBaseStylesChange = useCallback((styleIds: string[]) => {
    // If "all" is selected, expand to all base style IDs
    if (styleIds.includes('all')) {
      setSelectedBaseStyleIds(['all']);
    } else {
      setSelectedBaseStyleIds(styleIds);
    }
  }, []);

  // Handle partner styles change
  const handlePartnerStylesChange = useCallback((styleIds: string[]) => {
    // If "all-partner" is selected, expand to all partner style IDs
    if (styleIds.includes('all-partner')) {
      setSelectedPartnerStyleIds(['all-partner']);
    } else {
      setSelectedPartnerStyleIds(styleIds);
    }
  }, []);

  // Save style selections
  const handleSaveStyles = useCallback(async () => {
    if (!partnerProduct) return;

    setIsSaving(true);

    // Expand "all" selections to actual style IDs
    let baseIds = selectedBaseStyleIds;
    if (baseIds.includes('all')) {
      baseIds = allBaseStyles.map(s => s.style_id).filter(Boolean) as string[];
    }

    let partnerIds = selectedPartnerStyleIds;
    if (partnerIds.includes('all-partner')) {
      partnerIds = allPartnerStyles
        .map(s => s.style_id)
        .filter(Boolean) as string[];
    }

    const allStyleIds = [...baseIds, ...partnerIds];

    const { error } = await handleTRPCRequest({
      method: TRPCMethodEnum.updateStyles,
      resourceType: TRPCResourceEnum.partnerProducts,
      requestBody: {
        partner_product_id: partnerProduct.partner_product_id,
        style_ids: allStyleIds
      }
    });

    if (!error) {
      toast.success('Styles saved successfully!');
      setOriginalBaseStyleIds(selectedBaseStyleIds);
      setOriginalPartnerStyleIds(selectedPartnerStyleIds);
      setUnsavedChanges(false);
    }

    setIsSaving(false);
  }, [
    partnerProduct,
    selectedBaseStyleIds,
    selectedPartnerStyleIds,
    allBaseStyles,
    allPartnerStyles,
    handleTRPCRequest
  ]);

  // Reset to default styles
  const handleResetStyles = useCallback(async () => {
    if (!partnerProduct) return;

    setIsSaving(true);
    setShowResetConfirm(false);

    const { error } = await handleTRPCRequest({
      method: TRPCMethodEnum.resetStyles,
      resourceType: TRPCResourceEnum.partnerProducts,
      requestBody: {
        partner_product_id: partnerProduct.partner_product_id
      }
    });

    if (!error) {
      toast.success('Styles reset to defaults!');
      // Refetch the styles
      const { res: fetchedStyles } = await handleTRPCRequest({
        method: TRPCMethodEnum.listStyles,
        resourceType: TRPCResourceEnum.partnerProducts,
        requestBody: {
          partner_product_id: partnerProduct.partner_product_id
        }
      });

      if (fetchedStyles) {
        const baseIds: string[] = [];
        const partnerIds: string[] = [];

        fetchedStyles.forEach((s: PartnerProductStyle) => {
          if (s.is_partner_style) {
            partnerIds.push(s.style_id);
          } else {
            baseIds.push(s.style_id);
          }
        });

        setSelectedBaseStyleIds(baseIds);
        setSelectedPartnerStyleIds(partnerIds);
        setOriginalBaseStyleIds(baseIds);
        setOriginalPartnerStyleIds(partnerIds);
      }
    }

    setIsSaving(false);
  }, [partnerProduct, handleTRPCRequest]);

  // Navigation handlers
  const handleBackToPartner = useCallback(() => {
    navigate(`/partners/${params.partner_id}`);
  }, [navigate, params.partner_id]);

  const handleBackToProduct = useCallback(() => {
    navigate(`/products/${params.product_id}`);
  }, [navigate, params.product_id]);

  // No-op handlers for read-only components
  const noopLabelSlotUpdate = useCallback(() => {}, []);
  const noopLabelListUpdate = useCallback(() => {}, []);
  const noopSetIsOption = useCallback(() => {}, []);
  const noopSetModalOpen = useCallback(() => {}, []);
  const noopSetTemplateSelectionsList = useCallback(() => {}, []);
  const noopUpdateUseMixedTemplate = useCallback(() => {}, []);

  // Dummy state for read-only materials select
  const [materialsSelectOpen, setMaterialsSelectOpen] = useState<{
    label_id: string;
    open: boolean;
  }>({ label_id: '', open: false });

  if (isSaving) {
    return <LoadingModalComponent isOpen={isSaving} message="Saving..." />;
  }

  if (isLoading || labelsLoading) {
    return <LoadingModalComponent isOpen={isLoading || labelsLoading} />;
  }

  if (!partnerProduct) {
    return (
      <div className="flex h-full flex-col items-center justify-center">
        <p className="text-light-gray mb-4 text-lg">
          Partner product not found
        </p>
        <Link to={`/partners/${params.partner_id}`} className="text-navy">
          Return to Partner
        </Link>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      <Title title="Partner Product Configuration" />

      {/* Breadcrumb */}
      <div className="text-light-gray text-sm">
        <Link to="/partners" className="hover:underline">
          Partners
        </Link>
        {' > '}
        <Link to={`/partners/${params.partner_id}`} className="hover:underline">
          {partnerName}
        </Link>
        {' > Products > '}
        <span className="text-dark-gray font-semibold">
          {baseProduct?.system_name}
        </span>
      </div>

      {/* System Name Display (read-only) */}
      <div className="flex items-center gap-3">
        <div className="text-input-shadow flex h-[2.5rem] flex-1 items-center rounded-lg px-3">
          <span className="text-dark-gray font-medium">
            {baseProduct?.system_name}
          </span>
        </div>
        <Chip backgroundColor="bg-blue-600" size={SizeEnum.medium}>
          Partner Product
        </Chip>
        <Link
          to={`/products/${params.product_id}`}
          className="text-navy text-sm hover:underline"
        >
          View/Edit Base Product →
        </Link>
      </div>

      {/* Style Selectors Row */}
      <div className="flex items-start gap-4">
        <BaseStylesMultiSelect
          isOpen={baseStylesSelectOpen}
          setIsOpen={setBaseStylesSelectOpen}
          selectedStyleIds={selectedBaseStyleIds}
          onStylesChange={handleBaseStylesChange}
        />
        <PartnerStylesMultiSelect
          isOpen={partnerStylesSelectOpen}
          setIsOpen={setPartnerStylesSelectOpen}
          partnerId={params.partner_id || ''}
          partnerName={partnerName}
          selectedStyleIds={selectedPartnerStyleIds}
          onStylesChange={handlePartnerStylesChange}
        />
        <div className="flex-1" />
        <ContainedButton
          color="secondary"
          onClick={() => setShowResetConfirm(true)}
        >
          Reset to Defaults
        </ContainedButton>
      </div>

      {/* Labels Section (read-only) */}
      <div className="items-top flex gap-5">
        <div className="w-1/2">
          <AddLabelsComponent
            materialsSelectOpen={materialsSelectOpen}
            setMaterialsSelectOpen={setMaterialsSelectOpen}
            isOption={isOption}
            labelsList={labels}
            setSelectedIndex={setSelectedIndex}
            setIsOption={noopSetIsOption}
            labelSlots={labelSlots}
            onUpdateLabelSlots={noopLabelSlotUpdate}
            onUpdateLabelList={noopLabelListUpdate}
            setModalOpen={noopSetModalOpen}
            error={[{ options: [] }]}
            readOnly
            readOnlyNote={
              <p className="text-light-gray text-sm">
                Label configuration is inherited from the base product.{' '}
                <Link
                  to={`/products/${params.product_id}`}
                  className="text-navy hover:underline"
                >
                  Edit base product labels →
                </Link>
              </p>
            }
          />
        </div>

        <DndProvider backend={HTML5Backend}>
          <GraphComponent
            selectedIndex={selectedIndex}
            setSelectedIndex={setSelectedIndex}
            isOption={isOption}
            labelSlots={labelSlots}
            onUpdateLabelList={noopLabelListUpdate}
            readOnly
          />
        </DndProvider>
      </div>

      {/* PDF Elements Section (read-only) */}
      <TemplateSelections
        hasBothLaminatedOptions={false}
        templateSelectionsList={baseProduct?.template_selections || []}
        setTemplateSelectionsList={noopSetTemplateSelectionsList}
        usingMixedTemplate={baseProduct?.use_mixed_template || false}
        updateUseMixedTemplate={noopUpdateUseMixedTemplate}
        readOnly
        readOnlyNote={
          <p className="text-light-gray text-sm">
            PDF Elements are inherited from the base product.{' '}
            <Link
              to={`/products/${params.product_id}`}
              className="text-navy hover:underline"
            >
              Edit base product PDF elements →
            </Link>
          </p>
        }
      />

      <div className="grow" />

      {/* Action Buttons */}
      <div className="mt-10 flex gap-3">
        {cameFromProduct ? (
          <ContainedButton
            className="flex-1"
            onClick={handleBackToProduct}
            size={SizeEnum.large}
            color="secondary"
          >
            Back to Product
          </ContainedButton>
        ) : (
          <ContainedButton
            className="flex-1"
            onClick={handleBackToPartner}
            size={SizeEnum.large}
            color="secondary"
          >
            Back to Partner
          </ContainedButton>
        )}
        {/* Show both if we came from product for convenience */}
        {cameFromProduct && (
          <ContainedButton
            className="flex-1"
            onClick={handleBackToPartner}
            size={SizeEnum.large}
            color="secondary"
          >
            Back to Partner
          </ContainedButton>
        )}
        <ContainedButton
          className="flex-1"
          onClick={handleSaveStyles}
          disabled={!unsavedChanges || isSaving}
          size={SizeEnum.large}
        >
          {isSaving ? 'Saving...' : 'Save'}
        </ContainedButton>
      </div>

      <UnsavedChangesModalComponent
        unsavedChanges={unsavedChanges}
        unsavedChangesBlocker={unsavedChangesBlocker}
      />

      {/* Reset Confirmation Modal */}
      <ConfirmModalComponent
        isOpen={showResetConfirm}
        onCancel={() => setShowResetConfirm(false)}
        onConfirm={handleResetStyles}
        title="Reset Styles to Defaults"
        message="This will reset the style selection to include all base product styles and all partner-specific styles. Any custom selection will be lost."
        confirmText="Reset"
        cancelText="Cancel"
        contentLabel="Reset Styles Confirmation"
      />
    </div>
  );
}
